import { BucketData, PaymentPeriod, PeriodLicense } from 'src/types/tax';
import {
  downloadBlob,
  stringifyToSpreadsheet,
} from '../../../common/spreadsheetUtils';
import { calculateChecksum, COLUMN_NAMES, TemplateRecord } from './utils';

const HIDDEN_COLUMNS: Array<COLUMN_NAMES> = [
  COLUMN_NAMES.CHECKSUM,
  COLUMN_NAMES.ROW_ID,
];

const DOWNLOAD_TEMPLATE_COLUMNS_NAME = 'TaxableReceipts.xlsx';

const buildRecordForPeriod = (
  { id, licenses, startDate, name: label }: PaymentPeriod,
  bucketPerPeriod: Record<string, Array<BucketData>>,
  includeDeductions?: boolean
): Array<TemplateRecord> =>
  licenses.reduce(
    (prev: Array<TemplateRecord>, licenseReport: PeriodLicense) => {
      const licenseRecord =
        bucketPerPeriod[startDate]?.map((bucket: BucketData) => ({
          [COLUMN_NAMES.PERIOD]: label?.trim() ?? '',
          [COLUMN_NAMES.PERMIT_NUMBER]: licenseReport.license?.trim() ?? '',
          [COLUMN_NAMES.PROPERTY_ADDRESS]: licenseReport?.address?.trim() ?? '',
          [COLUMN_NAMES.PROVIDER]: bucket.key?.trim() ?? '',
          [COLUMN_NAMES.TOTAL_REVENUE]: undefined,
          ...(includeDeductions
            ? { [COLUMN_NAMES.ALLOWABLE_DEDUCTIONS]: undefined }
            : {}),
          [COLUMN_NAMES.ROW_ID]: btoa(
            JSON.stringify({
              periodId: id,
              licenseId: licenseReport.license,
              provider: bucket.key,
            })
          ),
        })) || [];

      return [...prev, ...licenseRecord];
    },
    []
  );

export const getDownloadTemplateRecords = (
  periods: Array<PaymentPeriod>,
  bucketPerPeriod: Record<string, Array<BucketData>>,
  includeDeductions?: boolean
): Promise<Array<TemplateRecord>> => {
  const records = periods.reduce(
    (records: Array<TemplateRecord>, period: PaymentPeriod) => [
      ...records,
      ...buildRecordForPeriod(period, bucketPerPeriod, includeDeductions),
    ],
    []
  );

  return Promise.all(
    records.map(async (record) => ({
      ...record,
      [COLUMN_NAMES.CHECKSUM]: await calculateChecksum(record),
    }))
  );
};

export async function generateAndDownloadRecords(
  periods: Array<PaymentPeriod>,
  bucketPerPeriod: Record<string, Array<BucketData>>,
  includeDeductions?: boolean
): Promise<void> {
  const records = await getDownloadTemplateRecords(
    periods,
    bucketPerPeriod,
    includeDeductions
  );

  const columns = Object.values(COLUMN_NAMES)
    .filter((column) =>
      includeDeductions ? true : column !== COLUMN_NAMES.ALLOWABLE_DEDUCTIONS
    )
    .map((column) => ({
      key: column,
      hide: HIDDEN_COLUMNS.includes(column),
    }));

  const blob = await stringifyToSpreadsheet(records, columns);
  downloadBlob(blob, DOWNLOAD_TEMPLATE_COLUMNS_NAME);
}
